import { FormAdapter, FormAlert, FormContainerOptions, FormControlType, FormValues, FormControlProperties } from '@omegagrid/form';
import { msg } from "@omegagrid/localize";
import rp from '@reporting/rp-app-host';
import { JobStep } from '@reporting/rp-types/jobs';
import { JobStepAction } from '../types';

export class StepFormAdapter extends FormAdapter {

	constructor(
		private step: JobStep,
	) { super(); }

	getColumns() {
		return [
			{ width: 170 },
			{ width: null },
		];
	}

	async getControls() {
		let r = 0;
		const onResultList = [
			{ key: `${JobStepAction.QuitWithSuccess}`, value: msg('Quit with success') },
			{ key: `${JobStepAction.QuitWithFailure}`, value: msg('Quit with failure') },
			{ key: `${JobStepAction.GoToNextStep}`, value: msg('Go to next step') },
		];
		const job = await rp.ms('jobs').get<JobStep[]>('steps', { job_name: this.step?.job_name });
		for (const step of job) {
			onResultList.push({ key: `${JobStepAction.GoToStep}:${step.step_id}`, value: `${msg('Go to step')}: [${step.step_id}] ${step.step_name}` });
		}
		const controls: FormControlProperties[] = [
			{ row: r, col: 0, type: FormControlType.Label, value: msg('Name'), for: 'step_name' },
			{ row: r++, col: 1, type: FormControlType.TextInput, key: 'step_name', value: this.step?.step_name || null, required: true },
			{ row: r, col: 0, type: FormControlType.Label, value: msg('On success'), for: '_on_success' },
			{
				row: r++, col: 1, type: FormControlType.List, key: '_on_success', required: true,
				value: this.serializeStepAction(this.step?.on_success_action, this.step?.on_success_step_id),
				content: { sourceData: onResultList, options: {selectMode: 'single', autoClose: true} }
			},
			{ row: r, col: 0, type: FormControlType.Label, value: msg('On failure'), for: '_on_fail' },
			{
				row: r++, col: 1, type: FormControlType.List, key: '_on_fail', required: true,
				value: this.serializeStepAction(this.step?.on_fail_action, this.step?.on_fail_step_id),
				content: { sourceData: onResultList, options: {selectMode: 'single', autoClose: true} }
			},
			{ row: r, col: 0, type: FormControlType.Label, value: msg('Retry attempts'), for: 'retry_attempts' },
			{ row: r++, col: 1, type: FormControlType.List, key: 'retry_attempts', value: this.step?.retry_attempts || 0, required: true, content: {
				sourceData: Array.from({ length: 11 }, (_, i) => ({ k: i, v: i })),
				options: {selectMode: 'single', autoClose: true}
			}},
			{ row: r, col: 0, type: FormControlType.Label, value: `${msg('Retry interval')} (${msg('min')})`, for: 'retry_interval' },
			{ row: r++, col: 1, type: FormControlType.List, key: 'retry_interval', value: this.step?.retry_interval || 0, required: true, content: {
				sourceData: Array.from({ length: 61 }, (_, i) => ({ k: i, v: i })),
				options: {selectMode: 'single', autoClose: true}
			}},
		];
		return controls;
	}

	serializeStepAction(action?: number, stepId?: number): string {
		if (!action) return `${JobStepAction.GoToNextStep}`;
		if (action === JobStepAction.GoToStep && stepId) return `${action}:${stepId}`;
		return `${action}`;
	}

	parseStepAction(value: string): { action: number, stepId?: number } {
		const parts = value.split(':');
		const action = parseInt(parts[0]);
		const stepId = parts.length > 1 ? parseInt(parts[1]) : null;
		return { action, stepId };
	}

	getOptions(): FormContainerOptions {
		return {
			caption: null,
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			buttons: ["save", "close"]
		};
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean|FormAlert[]> {
		const formValues = values.get(0);
		this.step.step_name = formValues['step_name'] as string;
		this.step.on_success_action = this.parseStepAction(formValues['_on_success'] as string).action;
		this.step.on_success_step_id = this.parseStepAction(formValues['_on_success'] as string).stepId;
		this.step.on_fail_action = this.parseStepAction(formValues['_on_fail'] as string).action;
		this.step.on_fail_step_id = this.parseStepAction(formValues['_on_fail'] as string).stepId;
		this.step.retry_attempts = formValues['retry_attempts'] as number;
		this.step.retry_interval = formValues['retry_interval'] as number;
		await rp.ms('jobs').post('steps', this.step);
		return true;
	}
};
